import React from 'react'
import { GraphQLError } from 'graphql'
import { errorReporter } from '../../utils/errorReporter'
import { ErrorPage } from './ErrorPage'
import { InlineError } from './InlineError'

interface ErrorBoundaryProps {
  error?: Error | GraphQLError | null
  onRetry?: () => void
  fullHeight?: boolean
  children?: React.ReactNode
  /**
   * 호출부에서 `errorReporter.capture()`를 먼저 호출해 errorId를 얻은 경우 여기로 전달.
   * 전달되면 InlineError 카드에 Error ID가 노출되어 CS 문의 시 식별 가능.
   * 전달되지 않으면 Apollo `errorLink`가 자동 캡처는 수행하지만 해당 errorId는
   * 이 컴포넌트까지 전파되지 않으므로 UI에 표시되지 않는다. (중복 캡처 방지 트레이드오프)
   */
  errorId?: string
}

/**
 * 기존 48개 호출부 호환 shell. 사용 방식 두 가지:
 *
 * 1. **인라인 에러 노출**: `<ErrorBoundary error={error} onRetry={refetch} />`
 *    → 내부적으로 `<InlineError ...>`로 위임. 카테고리 기반 사용자 친화 메시지 표시.
 *    Error ID 노출이 필요하면 호출부에서 `errorReporter.capture()` 후 `errorId` prop 전달.
 *
 * 2. **전역 렌더 크래시 캐치**: `<ErrorBoundary>{children}</ErrorBoundary>`는
 *    지원하지 않는다 — 그 용도로는 아래 `ErrorBoundaryWrapper` 사용.
 *
 * 하위 호환을 위해 `children`이 전달되면 그대로 렌더한다 (error가 없을 때).
 */
export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  error,
  onRetry,
  fullHeight = true,
  errorId,
  children,
}) => {
  if (!error) return <>{children}</>
  return (
    <InlineError
      error={error}
      onRetry={onRetry}
      fullHeight={fullHeight}
      errorId={errorId}
    />
  )
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorId: string | null
}

/**
 * React Error Boundary 클래스 컴포넌트. 렌더 중 예외를 잡아 Level 1
 * `ErrorPage`로 전환하고, 동시에 `errorReporter`로 백엔드에 전송한다.
 */
export class ErrorBoundaryWrapper extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null, errorId: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorId: null }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const captured = errorReporter.capture(error, {
      source: 'REACT_RENDER',
      componentStack: errorInfo.componentStack ?? undefined,
    })
    this.setState({ errorId: captured.errorId })
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorPage
          errorId={this.state.errorId ?? undefined}
          error={this.state.error}
          onRetry={() => window.location.reload()}
        />
      )
    }
    return this.props.children
  }
}
